import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  CBadge,
  CDropdown,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilBadge, cilBell, cilCommentSquare, cilUserFollow, cilUserUnfollow } from '@coreui/icons'
import { useOutstandingActions } from '../../api/hooks/useOutstandingActions'
import type {
  OutstandingAction,
  OutstandingActionKey,
} from '../../api/types/outstanding-actions.types'
import routePaths from '../../routes/route-paths'

/**
 * The API returns a key, never a URL, so routing stays owned by the client. Typed as a total record:
 * adding a key to OutstandingActionKey without a route here is a compile error rather than a dead
 * link discovered in production.
 */
const ACTION_ROUTES: Record<OutstandingActionKey, string> = {
  accessToGrant: routePaths.AccessToGrant,
  accessToRemove: routePaths.AccessToRemove,
  outstandingCustomerFeedback: routePaths.CustomerFeedback,
  membershipRequests: routePaths.MembershipRequests,
}

const ACTION_ICONS: Record<OutstandingActionKey, string | string[]> = {
  accessToGrant: cilUserFollow,
  accessToRemove: cilUserUnfollow,
  outstandingCustomerFeedback: cilCommentSquare,
  membershipRequests: cilBadge,
}

const AppHeaderNotifications: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, isError } = useOutstandingActions()

  const actions = data?.actions ?? []
  const total = data?.totalCount ?? 0

  // Waiting time reads better in days once it runs past a day; below that the hours are the useful bit.
  const formatAge = (ageHours: number | null) => {
    if (ageHours === null) return null
    if (ageHours < 1) return t('common:outstandingActions.waitingUnderAnHour')
    if (ageHours < 24) return t('common:outstandingActions.waitingHours', { count: Math.floor(ageHours) })
    return t('common:outstandingActions.waitingDays', { count: Math.floor(ageHours / 24) })
  }

  const openAction = (action: OutstandingAction) => navigate(ACTION_ROUTES[action.key])

  return (
    <CDropdown variant="nav-item" placement="bottom-end">
      {/* position-relative anchors the badge to the toggle; without it CoreUI's top-end positioning
          resolves against an ancestor and the count drifts off the bell. */}
      <CDropdownToggle
        caret={false}
        className="position-relative"
        aria-label={t('common:outstandingActions.title', { count: total })}
      >
        <CIcon icon={cilBell} size="lg" />
        {/* Hidden entirely at zero: a grey "0" would make an empty queue look like something to do. */}
        {total > 0 && (
          <CBadge
            color="danger"
            position="top-end"
            shape="rounded-pill"
            style={{ fontSize: '0.65rem' }}
          >
            {total}
          </CBadge>
        )}
      </CDropdownToggle>
      <CDropdownMenu className="pt-0" style={{ minWidth: '20rem' }}>
        <CDropdownHeader className="bg-body-secondary fw-semibold mb-2">
          {total > 0
            ? t('common:outstandingActions.title', { count: total })
            : t('common:outstandingActions.titleEmpty')}
        </CDropdownHeader>

        {isError && (
          <CDropdownItem disabled className="text-body-secondary">
            {t('common:outstandingActions.error')}
          </CDropdownItem>
        )}

        {/* Clicking the bell with nothing outstanding should say so rather than open an empty menu. */}
        {!isError && actions.length === 0 && (
          <CDropdownItem disabled className="text-body-secondary">
            {t('common:outstandingActions.empty')}
          </CDropdownItem>
        )}

        {actions.map((action) => (
          <CDropdownItem
            key={action.key}
            as="button"
            type="button"
            onClick={() => openAction(action)}
            className="d-flex align-items-start gap-2 py-2"
          >
            <CIcon icon={ACTION_ICONS[action.key]} className={`mt-1 text-${action.severity}`} />
            <span className="d-flex flex-column">
              <span className="d-flex align-items-center gap-2">
                <span>{t(`common:outstandingActions.categories.${action.key}`)}</span>
                <CBadge color={action.severity}>{action.count}</CBadge>
              </span>
              {formatAge(action.oldestItemAgeHours) && (
                <small className="text-body-secondary">
                  {t('common:outstandingActions.oldest', {
                    age: formatAge(action.oldestItemAgeHours),
                  })}
                </small>
              )}
            </span>
          </CDropdownItem>
        ))}
      </CDropdownMenu>
    </CDropdown>
  )
}

export default AppHeaderNotifications
